import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, num } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_NUM, fileNameConFecha } from '../lib/xlsx';
import type { Insumo } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';

type FiltroStock = 'todos' | 'con-stock' | 'agotados' | 'negativos';

export function InventarioPage() {
  const { tiene } = useAuth();
  const [filtro, setFiltro] = useState<FiltroStock>('todos');
  const [busqueda, setBusqueda] = useState('');

  const q = useQuery({
    queryKey: ['insumos'],
    queryFn: () => API.get<Insumo[]>('/insumos'),
    enabled: tiene('insumos:read'),
  });

  const inventario = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return (q.data ?? [])
      .filter((i) => i.conStock)
      .filter((i) => {
        const stock = i.stockActual ?? 0;
        if (filtro === 'con-stock') return stock > 0;
        if (filtro === 'agotados') return stock === 0;
        if (filtro === 'negativos') return stock < 0;
        return true;
      })
      .filter((i) => {
        if (!texto) return true;
        return [i.nombre, i.unidad, i.categoria?.nombre ?? '']
          .some((v) => v.toLowerCase().includes(texto));
      });
  }, [q.data, filtro, busqueda]);

  const resumen = useMemo(() => {
    const base = (q.data ?? []).filter((i) => i.conStock);
    let valorTotal = 0;
    let conStock = 0;
    let agotados = 0;
    let negativos = 0;

    for (const i of base) {
      const stock = i.stockActual ?? 0;
      const precio = i.precioActual ?? 0;
      if (stock > 0) conStock += 1;
      else if (stock < 0) negativos += 1;
      else agotados += 1;
      if (stock > 0 && precio > 0) valorTotal += stock * precio;
    }

    return { total: base.length, conStock, agotados, negativos, valorTotal };
  }, [q.data]);

  function exportarXlsx() {
    if (inventario.length === 0) return;
    downloadXlsx(fileNameConFecha('inventario'), [
      {
        name: 'Inventario',
        rows: inventario,
        columns: [
          { header: 'Insumo', value: (i) => i.nombre, width: 24 },
          { header: 'Categoria', value: (i) => i.categoria?.nombre ?? '', width: 18 },
          { header: 'Unidad', value: (i) => i.unidad, width: 10 },
          { header: 'Comprado', value: (i) => i.compradoTotal ?? 0, width: 12, numFmt: FMT_NUM },
          { header: 'Consumido', value: (i) => i.consumidoTotal ?? 0, width: 12, numFmt: FMT_NUM },
          { header: 'Stock actual', value: (i) => i.stockActual ?? 0, width: 12, numFmt: FMT_NUM },
          { header: 'Precio unitario', value: (i) => i.precioActual ?? 0, width: 14, numFmt: FMT_COP },
          {
            header: 'Valor inventario',
            value: (i) => Math.max(0, i.stockActual ?? 0) * (i.precioActual ?? 0),
            width: 16,
            numFmt: FMT_COP,
          },
        ],
      },
    ]);
  }

  const columns: Column<Insumo>[] = [
    {
      key: 'insumo',
      header: 'Insumo',
      mobile: 'primary',
      render: (i) => (
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{i.nombre}</div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-slate-500">
            <span>{i.unidad}</span>
            {i.categoria?.nombre && <span className="pill bg-slate-100 text-slate-600">{i.categoria.nombre}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'stock',
      header: 'Stock actual',
      mobile: 'secondary',
      align: 'right',
      render: (i) => {
        const stock = i.stockActual ?? 0;
        const cls = stock > 0 ? 'text-emerald-700' : stock < 0 ? 'text-red-600' : 'text-slate-500';
        return <span className={`font-semibold ${cls}`}>{num(stock, 2)} {i.unidad}</span>;
      },
    },
    { key: 'comprado', header: 'Comprado', align: 'right', render: (i) => `${num(i.compradoTotal ?? 0, 2)} ${i.unidad}` },
    { key: 'consumido', header: 'Consumido', align: 'right', render: (i) => `${num(i.consumidoTotal ?? 0, 2)} ${i.unidad}` },
    {
      key: 'precio',
      header: 'Precio unit.',
      align: 'right',
      render: (i) => (
        <span>
          {cop(i.precioActual)}<span className="text-slate-400 text-xs"> /{i.unidad}</span>
        </span>
      ),
    },
    {
      key: 'valor',
      header: 'Valor',
      align: 'right',
      render: (i) => {
        const valor = Math.max(0, i.stockActual ?? 0) * (i.precioActual ?? 0);
        return <span className="font-medium">{cop(valor)}</span>;
      },
    },
  ];

  const activeFilters = (filtro !== 'todos' ? 1 : 0) + (busqueda.trim() ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Inventario"
        subtitle="Existencias actuales de materia prima: compras registradas menos consumos de producción."
      />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Insumos con stock</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{resumen.total}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Disponibles</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-emerald-700">{resumen.conStock}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Sin stock / negativos</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-red-600">{resumen.agotados + resumen.negativos}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Valor estimado</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{cop(resumen.valorTotal)}</div>
        </div>
      </section>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200 space-y-3 bg-slate-50/60 rounded-t-lg">
          <FilterBar activeCount={activeFilters}>
            <div>
              <label className="label">Buscar</label>
              <input
                className="input"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Maiz, bolsas..."
              />
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={filtro} onChange={(e) => setFiltro(e.target.value as FiltroStock)}>
                <option value="todos">Todos</option>
                <option value="con-stock">Con stock</option>
                <option value="agotados">Agotados</option>
                <option value="negativos">Stock negativo</option>
              </select>
            </div>
          </FilterBar>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-600">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span><b>{inventario.length}</b> registros visibles</span>
              {resumen.negativos > 0 && <span className="text-red-600 font-medium">{resumen.negativos} con stock negativo</span>}
            </div>
            <button
              type="button"
              className="btn-secondary w-full sm:w-auto"
              onClick={exportarXlsx}
              disabled={inventario.length === 0}
            >
              Exportar .xlsx
            </button>
          </div>
        </div>

        <ResponsiveTable<Insumo>
          columns={columns}
          rows={inventario}
          rowKey={(i) => i.id}
          loading={q.isLoading}
          empty="Sin inventario para mostrar."
        />
      </div>
    </div>
  );
}
